import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BASE_URL } from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const QUESTIONS_PER_PAGE = 10;

const TagQuestions = () => {
  const { tagName } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchQuestions = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(`${BASE_URL}/questions/tag/${encodeURIComponent(tagName)}/`);
        if (!response.ok) {
          throw new Error(`Failed to fetch questions for tag "${tagName}"`);
        }
        const data = await response.json();
        setQuestions(data);
        setCurrentPage(1);
      } catch (err) {
        console.error(err);
        setError(err.message || 'An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [tagName]);

  const totalPages = Math.ceil(questions.length / QUESTIONS_PER_PAGE);

  const handlePageChange = (pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
    }
  };

  const handleQuestionClick = (id) => {
    navigate(`/dashboard/questions/${id}`);
  };

  const cleanDetails = (details) => {
    if (!details) return null;
    return details.replace(/<\/?[^>]+(>|$)/g, "");
  };

  const paginatedQuestions = questions.slice(
    (currentPage - 1) * QUESTIONS_PER_PAGE,
    currentPage * QUESTIONS_PER_PAGE
  );

  if (loading) return (
    <div className="container mx-auto py-6">
      <p className="text-muted-foreground">
        Loading questions tagged with "<strong>{tagName}</strong>"...
      </p>
    </div>
  );
  
  if (error) return (
    <div className="container mx-auto py-6">
      <p className="text-destructive">Error: {error}</p>
    </div>
  );
  
  if (!questions.length) return (
    <div className="container mx-auto py-6">
      <p className="text-muted-foreground">No questions found with tag "{tagName}".</p>
    </div>
  );

  return (
    <div className="container mx-auto py-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>
            Questions tagged with <span className="text-primary">{tagName}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
        {paginatedQuestions.map((q) => {
          const formattedTags = Array.isArray(q.tags)
            ? q.tags.map(t => (typeof t === 'object' && t.name ? t.name : t)).join(', ')
            : typeof q.tags === 'string'
              ? q.tags.split(',').join(', ')
              : 'No tags';

          const postedBy = q.posted_by || q.user?.email || "Unknown";

          return (
                <Card 
                  key={q.id} 
                  className="cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={() => handleQuestionClick(q.id)}
                >
                  <CardContent className="pt-6">
              {q.image && (
                      <img 
                        src={q.image} 
                        alt={q.title || 'Question Image'} 
                        className="w-full h-48 object-cover rounded-md mb-4"
                      />
              )}
                    <h3 className="text-xl font-semibold mb-2">{q.title || 'Untitled Question'}</h3>
                    <p className="text-muted-foreground mb-4">
                {cleanDetails(q.details)?.length > 150
                  ? cleanDetails(q.details).slice(0, 150) + '...'
                  : cleanDetails(q.details) || <em>No details provided</em>}
              </p>
                    <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                      <div>
                <strong>Tags: </strong>
                <span>{formattedTags}</span>
              </div>
                      <Separator orientation="vertical" className="h-4" />
                      <div>
                        <strong>Posted by: </strong>
                        <span>{postedBy}</span>
              </div>
            </div>
                  </CardContent>
                </Card>
          );
        })}
      </div>

      {totalPages > 1 && (
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
                Previous
              </Button>
              {[...Array(totalPages)].map((_, index) => (
                <Button
                  key={index + 1}
                  variant={currentPage === index + 1 ? "default" : "outline"}
                  onClick={() => handlePageChange(index + 1)}
              >
                  {index + 1}
                </Button>
              ))}
              <Button
                variant="outline"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
              </Button>
        </div>
      )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TagQuestions;
